<template>
  <Modal :color="ConfigModalsColor" max_width="700" title="Finalize your quote..." :visible="true" @close="CloseModal">
    <v-card-text>
      <div v-if="!form_success">
        <v-form id="quote-request-form">
          <v-row v-if="form_step==1">
            <!--Plotter/Wide-format Questionnaire-->
            <template v-if="active_form=='wide-format-form'">
              <QuoteFormTitle>Plotter/Wide-format Printer Questionnaire</QuoteFormTitle>
              <HowManyRolls/>
              <PrintsPerMonth/>
              <TypeOfPrinting/>
              <PrintingDims/>
              <NeedScanner/>
            </template>

            <!--Quadient Fields-->
            <template v-else-if="active_form=='mailing-form'">
              <QuoteFormTitle>Mailing Equipment Questionnaire</QuoteFormTitle>
              <LeaseTermSelector/>
              <CopierQuantityMailing/>
              <OwnsMailingMachine/>
              <NumMailsPerMonth/>
            </template>

            <!--Phone Fields-->
            <template v-else-if="active_form=='phone-form'">
              <QuoteFormTitle>Phone System Questionnaire</QuoteFormTitle>
              <PhoneSysReason/>
              <PhoneQuantity/>
            </template>

            <!--'default-form' Copier Questionnaire-->
            <template v-else>
              <QuoteFormTitle>Copier Questionnaire</QuoteFormTitle>
              <LeaseTermSelector/>
              <CopierQuantity/>
              <AvgPrintSlider/>
            </template>
          </v-row>
          <v-row v-if="form_step==2">
            <QuoteFormTitle>Company Details</QuoteFormTitle>
            <CompanyFields/>
          </v-row>
          <v-row v-if="form_step==3">
            <AddressFields/>
          </v-row>
          <v-card v-if="errors.length>0" elevation="0" border class="pl-5 mt-3" color="rgba(229,43,43,0.08)">
            <v-card-text>
              <ul class="error-list animate-moveFromTop">
                <li class="text-red" v-for="error in errors" :key="error">{{ error }}</li>
              </ul>
            </v-card-text>
          </v-card>
        </v-form>
      </div>
      <div v-else>
        <QuoteSuccess/>
      </div>
    </v-card-text>
    <template v-if="!form_success" #footer>
      <v-btn :color="ConfigElementsColor" rounded="pill" class="d-none d-sm-flex" :disabled="is_loading"
             @click="OpenCart" variant="tonal" prepend-icon="mdi-cart">({{ cart_items.length }}) items
      </v-btn>&nbsp; &nbsp;
      <v-btn :color="ConfigElementsColor" rounded="pill" class="d-none d-sm-flex" :disabled="is_loading"
             @click="CloseModal" variant="tonal" prepend-icon="mdi-plus-circle-outline">Add more...
      </v-btn>
      <v-spacer/>

      <v-btn :color="ConfigElementsColor" rounded="pill" class="align-sm-center mr-1" v-if="form_step>=2"
             :loading="is_loading" variant="tonal" @click="form_step--" prepend-icon="mdi-chevron-left">Go back
      </v-btn>&nbsp;
      <v-btn :color="ConfigElementsColor" rounded="pill" class="align-sm-center" v-if="form_step==3"
             :loading="is_loading" @click="SubmitQuote" id="request-a-quote-btn" prepend-icon="mdi-note-edit-outline">
        Request Quote
      </v-btn>
      <v-btn :color="ConfigElementsColor" rounded="pill" class="align-sm-center" v-if="form_step<=2"
             :loading="is_loading" @click="NextStep" append-icon="mdi-chevron-right">Continue
      </v-btn>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import Axios from 'axios'
import Server from '@/plugins/Server'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'

import Modal from '@/components/misc/Modal.vue'
import QuoteSuccess from '@/components/configurator/QuoteSuccess.vue'
import QuoteFormTitle from '@/components/catalog/QuoteFormTitle.vue'
import PhoneQuantity from '@/components/catalog/form/PhoneQuantity.vue'
import AvgPrintSlider from '@/components/catalog/form/AvgPrintSlider.vue'
import PhoneSysReason from '@/components/catalog/form/PhoneSysReason.vue'
import CopierQuantity from '@/components/catalog/form/CopierQuantity.vue'
import NumMailsPerMonth from '@/components/catalog/form/NumMailsPerMonth.vue'
import LeaseTermSelector from '@/components/catalog/form/LeaseTermSelector.vue'
import OwnsMailingMachine from '@/components/catalog/form/OwnsMailingMachine.vue'
import HowManyRolls from '@/components/catalog/form/HowManyRolls.vue'
import PrintsPerMonth from '@/components/catalog/form/PrintsPerMonth.vue'
import TypeOfPrinting from '@/components/catalog/form/TypeOfPrinting.vue'
import NeedScanner from '@/components/catalog/form/NeedScanner.vue'
import AddressFields from '@/components/catalog/form/AddressFields.vue'
import CompanyFields from '@/components/catalog/form/CompanyFields.vue'
import PrintingDims from '@/components/catalog/form/PrintingDims.vue'
import CopierQuantityMailing from '@/components/catalog/form/CopierQuantityMailing.vue'

const store = useGlobalsStore()
const {
  form_success,
  is_loading,
  form,
  cart_items,
  accessories,
  active_form,
  copier_types,
  ConfigModalsColor,
  ConfigElementsColor
} = storeToRefs(store)

const {
  SetError,
  SetSuccess,
  SetSelectedCopier,
  ClearQuoteValues,
  SetState
} = store

const errors = ref<string[]>([])
const form_step = ref(1)

const copier_color_text = computed(() => {
  let color_text = ''
  copier_types.value.forEach((item: any) => {
    if (form.value.copier.copier_type == item.value) {
      color_text = item.title
    }
  })
  return color_text
})

function BaseImage(product: any) {
  const url = (import.meta.env.VITE_SERVERAPI_URL || '') + '/storage/uploads/products'
  if (product?.base_image && product.base_image.length > 0) {
    return `${url}/base-img/${product.base_image}`
  } else {
    return `${url}/base-img/_placeholder_.png`
  }
}

function ProductImage(product: any) {
  const url = (import.meta.env.VITE_SERVERAPI_URL || '') + '/storage/uploads/products'
  if (product?.product_image != null && product.product_image.length > 0) {
    return `${url}/product-img/${product.product_image}`
  } else {
    return `${url}/product-img/_placeholder_.png`
  }
}

function AccessoryImage(accessory: any) {
  const url = (import.meta.env.VITE_SERVERAPI_URL || '') + '/storage/uploads/accessories/featured'
  if (accessory?.featured_image && accessory.featured_image.length > 0) {
    return `${url}/${accessory.featured_image}`
  } else {
    return `${url}/_placeholder_.png`
  }
}

function Accessory(dataset: any) {
  return accessories.value.find((acc: any) => acc.id === dataset.accessory_id)
}

function CleanedCartItems() {
  const items = JSON.parse(JSON.stringify(cart_items.value))
  items.forEach((item: any) => {
    if (item.product.product_image != null && !item.product.product_image.startsWith('http')) {
      item.product.product_image = ProductImage(item.product)
    }
    if (item.product.base_image != null && !item.product.base_image.startsWith('http')) {
      item.product.base_image = BaseImage(item.product)
    }

    const itemAccessories: any[] = []
    item.datasets.forEach((ds: any) => {
      if (ds.selected) {
        const acc = Accessory(ds)
        if (acc) {
          if (acc.featured_image && !acc.featured_image.startsWith('http')) {
            acc.featured_image = AccessoryImage(acc)
          }
          itemAccessories.push(acc)
        }
      }
    })
    delete item.datasets
    item.accessories = itemAccessories
  })
  return items
}

function CloseModal() {
  SetState('form_modal', false)
  SetState('cart_modal', false)
  SetState('configurator', false)
}

function OpenCart() {
  CloseModal()
  SetState('cart_modal', true)
}

function ValidEmail(email: string) {
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailPattern.test(email)
}

function SubmitQuote() {
  errors.value = []
  const hook = (window as any).catalog_config?.connectm8_crm_url
  const data: any = {
    quote: {
      quote_id: null,
      quote_pdf: null,
      cart_items: CleanedCartItems(),

      lease_term: form.value.copier.lease_term + ` Months`,
      copier_type: form.value.copier.copier_type,
      copier_quantity: form.value.copier.copier_quantity,
      prints_per_month: form.value.copier.prints_per_month,

      num_mails_per_month: form.value.copier.num_mails_per_month,
      owns_mailing_machine: form.value.copier.owns_mailing_machine,

      how_many_rolls: form.value.copier.how_many_rolls,
      need_a_scanner: form.value.copier.need_a_scanner,
      type_of_printing: form.value.copier.type_of_printing,
      printing_dims: form.value.copier.printing_dims,

      phone_sys_reason: form.value.phone.phone_sys_reason,
      phone_quantity: form.value.phone.phone_quantity,
    },
    lead: {
      address: form.value.address,
      contact: form.value.lead,
    },
  }

  if (Array.isArray(data.quote.type_of_printing) && data.quote.type_of_printing.length > 0) {
    data.quote.type_of_printing = data.quote.type_of_printing.join(', ')
  }

  if (form.value.address.city.length < 1) errors.value.push('City is required')
  if (form.value.address.state.length < 1) errors.value.push('State is required')
  if (form.value.address.street.length < 1) errors.value.push('Street is required')
  if (form.value.address.zip.length < 1) errors.value.push('Zip code is required')

  if (errors.value.length > 0) {
    return SetError("Please fill out the required fields.")
  }

  errors.value = []
  const formData = new FormData()
  formData.append('source', location.host)
  formData.append('quote-data', JSON.stringify(data))

  SetState('form_success', false)
  SetState('is_loading', true)

  Server.post('/quotetool/store', formData).then(res => {
    console.log("/quotetool/store: ", res.data)
    if (res.data.result) {
      data.quote.quote_id = res.data.quote_id
      data.quote.quote_pdf = res.data.quote_pdf
      if (hook) {
        Axios.post(hook, data).then(res => {
          console.log("ConnectM8 Hook: ", res.data)
          SetSuccess('Your quote has been created!')
          SetState('form_success', true)
          SetState('configurator', false)
          SetState('cart_modal', false)
          SetSelectedCopier(null)
        }).catch(error => {
          console.log(error)
          alert("Sorry, an error occured on our end, please try again later.")
        }).finally(() => {
          SetState('is_loading', false)
          ClearQuoteValues()
        })
      } else {
        SetSuccess('Your quote has been created!')
        SetState('form_success', true)
        SetState('configurator', false)
        SetState('cart_modal', false)
        SetSelectedCopier(null)
        SetState('is_loading', false)
        ClearQuoteValues()
      }
    } else {
      SetError('Sorry, an error occurred. please try again later.')
      SetState('is_loading', false)
    }
  }).catch(error => {
    console.log(error)
    SetError('Sorry, an error occurred. please try again later.')
    SetState('is_loading', false)
  })
}

function NextStep() {
  errors.value = []
  if (form_step.value == 1) {
    const formName = active_form.value
    if (formName == 'wide-format-form') {
      if (!form.value.copier.how_many_rolls) errors.value.push('How many rolls do you want your printer to have?')
      if (!form.value.copier.prints_per_month) {
        errors.value.push('How many prints do you print per month?')
      }
      if (form.value.copier.type_of_printing.length < 1) errors.value.push('What type of printing do you do?')
      if (!form.value.copier.printing_dims) errors.value.push('Approximately how wide will the majority of your prints be?')
      if (!form.value.copier.need_a_scanner) errors.value.push('How handy would a scanner be for your business?')
    }

    if (formName == 'default-form') {
      if (form.value.copier.copier_quantity <= 0) errors.value.push('How many printers do you neeed?')
      if (form.value.copier.prints_per_month <= 0) {
        errors.value.push('Approximate monthly print/copy volume')
      }
    }

    if (formName == 'phone-form') {
      if (!form.value.phone.phone_sys_reason) errors.value.push('Which describes your phone system needs?')
      if (!form.value.phone.phone_quantity) errors.value.push('Approximately, how many phones do you need?')
    }

    if (formName == 'mailing-form') {
      if (form.value.copier.copier_quantity <= 0) errors.value.push('How many printers do you neeed?')
      if (form.value.copier.num_mails_per_month <= 0) errors.value.push('How many pieces of mail are you sending per month?')
      if (!form.value.copier.owns_mailing_machine) errors.value.push('Do you currently own a mailing machine?')
    }
  }
  if (form_step.value == 2) {
    if (!ValidEmail(form.value.lead.email)) errors.value.push('Email is required')
    if (form.value.lead.firstname.length < 1) errors.value.push('First name is required')
    if (form.value.lead.lastname.length < 1) errors.value.push('Last name is required')
    if (form.value.lead.phone.length < 1) errors.value.push('Phone number is required')
  }
  if (errors.value.length < 1) {
    form_step.value++
  } else {
    SetError("Please fill out the required fields.")
  }
}

onMounted(() => {
  if (cart_items.value.length > 0) {
    form.value.copier.copier_type = cart_items.value[0].product.copier_type
  }

  const form_copy = { ...form.value }
  const phone_qty = cart_items.value.filter((cart_item: any) => cart_item.product.printer_category.toLowerCase().includes('phone')).length
  const copier_qty = cart_items.value.filter((cart_item: any) => !cart_item.product.printer_category.toLowerCase().includes('phone')).length

  if (phone_qty > 0 && phone_qty <= 3) {
    form_copy.phone.phone_quantity = '1 To 3'
  } else if (phone_qty > 3 && phone_qty <= 49) {
    form_copy.phone.phone_quantity = '4 To 49'
  } else if (phone_qty > 49) {
    form_copy.phone.phone_quantity = '50+'
  }
  form_copy.copier.copier_quantity = copier_qty
  SetState('form', form_copy)

  if (active_form.value == 'scanner-form') {
    form_step.value = 2
  }
})
</script>
